import { FC } from 'react';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Image } from 'antd';
import ReactHtmlParser from 'react-html-parser';
import ReactPlayer from 'react-player';
import styled from 'styled-components';
import { useThemeContext, themeConfig } from '@/shared/theme';
import { ChatMode } from '@/shared/types';
import { ChatMessage, ChatMessageType } from '../model/types';

type Props = {
  message: ChatMessage;
  chatMode?: ChatMode;
  isMyMessage: (createdBy: ChatMessageType, chatMode?: ChatMode) => boolean;
};

const formatDate = (date: string): string => {
  return Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(date));
};

export const ChatMessageComponent: FC<Props> = ({
  message,
  chatMode,
  isMyMessage,
}) => {
  const { theme } = useThemeContext();
  const { id, text, assets, created, error } = message;

  return (
    <Wrapper>
      <Message key={id} isMyMessage={isMyMessage(created.by.type, chatMode)}>
        {ReactHtmlParser(text.replaceAll('\n', '<br />'))}
        {assets?.map(({ id, originalName, url }) =>
          ReactPlayer.canPlay(url) ? (
            <ReactPlayer
              controls
              key={url}
              alt={originalName}
              url={url}
              width="100%"
              height="100%"
            />
          ) : (
            <Image
              key={id}
              alt={originalName}
              src={url}
              preview={false}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
            />
          ),
        )}
      </Message>
      <MessageCreatedAt isMyMessage={isMyMessage(created.by.type, chatMode)}>
        {created?.at && formatDate(created.at)}
      </MessageCreatedAt>
      {error && (
        <div
          style={{
            fontSize: 10,
            color: themeConfig[theme].colors.error,
            textAlign: 'end',
          }}
        >
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          Failed to send
        </div>
      )}
    </Wrapper>
  );
};

const Wrapper = styled.div`
  margin-bottom: 15px;
`;

const Message = styled.div<{ isMyMessage: boolean }>`
  background: ${({ isMyMessage }) => (isMyMessage ? '#ffed66' : '#f0f0f0')};
  margin-left: ${({ isMyMessage }) => isMyMessage && 'auto'};
  color: #000;
  margin-bottom: 5px;
  padding: 5px 8px;
  font-size: 12px;
  border-radius: 6px;
  width: fit-content;
  max-width: 350px;
`;

const MessageCreatedAt = styled.div<{ isMyMessage: boolean }>`
  font-size: 10px;
  color: #aaa;
  text-align: ${({ isMyMessage }) => isMyMessage && 'end'};
`;
